import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  getAdminUser,
  getCurrentUser,
  listAdminUsersPage,
  SYSTEM_SUPERUSER_ROLE_ID,
  type ListAdminUsersPageInput,
} from "./api";

const adminUserStaleTimeMs = 5 * 60 * 1000;

const currentUserQueryKey = ["devplane", "current-user"] as const;

export const adminUserKeys = {
  all: ["devplane", "admin-users"] as const,
  details: () => [...adminUserKeys.all, "detail"] as const,
  detail: (userId: string) => [...adminUserKeys.details(), userId] as const,
  pages: () => [...adminUserKeys.all, "page"] as const,
  page: (input: ListAdminUsersPageInput) =>
    [...adminUserKeys.pages(), input] as const,
};

export function currentUserQueryOptions() {
  return {
    queryKey: currentUserQueryKey,
    queryFn: () => getCurrentUser(),
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  };
}

export function invalidateCurrentUserQuery(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: currentUserQueryKey,
    exact: true,
  });
}

export async function refreshCurrentUserQuery(queryClient: QueryClient) {
  await queryClient.cancelQueries({
    queryKey: currentUserQueryKey,
    exact: true,
  });
  await queryClient.invalidateQueries({
    queryKey: currentUserQueryKey,
    exact: true,
    refetchType: "none",
  });
  return queryClient.fetchQuery(currentUserQueryOptions());
}

export function removeCurrentUserQuery(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: currentUserQueryKey, exact: true });
}

export function useCurrentUserQuery(enabled = true) {
  return useQuery({
    ...currentUserQueryOptions(),
    enabled,
  });
}

export function adminUserQueryOptions(userId: string) {
  return {
    queryKey: adminUserKeys.detail(userId),
    queryFn: () => getAdminUser(userId),
    staleTime: adminUserStaleTimeMs,
  };
}

export function useAdminUserQuery(userId: string) {
  return useQuery({
    ...adminUserQueryOptions(userId),
    enabled: Boolean(userId),
  });
}

export function adminUsersPageQueryOptions(input: ListAdminUsersPageInput) {
  return {
    queryKey: adminUserKeys.page(input),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      listAdminUsersPage(input, { signal }),
    staleTime: adminUserStaleTimeMs,
  };
}

export function useAdminUsersPageQuery(
  input: ListAdminUsersPageInput,
  enabled = true,
) {
  return useQuery({
    ...adminUsersPageQueryOptions(input),
    enabled,
  });
}

export function useCurrentUserAdminAccessQuery(enabled = true) {
  return useQuery({
    ...currentUserQueryOptions(),
    enabled,
    select: (user) =>
      user?.roles.some((role) => role.roleId === SYSTEM_SUPERUSER_ROLE_ID) ??
      false,
  });
}
